import { isWebPlatform } from '@eventbrite/checkout-postmessage-api';
import { HAS_WINDOW_LISTENERS } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import get from 'lodash/get';
import { push, replace } from 'react-router-redux';
import { setSubmitFailed, change } from 'redux-form';
import { joinPath } from '@eventbrite/path-utils';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import {
    getOrganizerQuestionsPage,
    getOrderSuccessAnimationPage,
} from '../../../utils/routes';
import { STATSIG_EVENT_NAMES } from '../../../constants/statsig';
import {
    abandonOrder as abandonOrderApi,
    getOrderEligibility as getOrderEligibilityApi,
    getPaymentHistory as getPaymentHistoryApi,
    updateOrder as updateOrderApi,
} from '../../../api/order';
import {
    getUserAccountDetails as getUserAccountDetailsApi,
    getCurrentUserAccountDetails,
} from '../../../api/users';
import { getSurvey } from '../../../selectors/survey';
import { logOrderSuccessEvents } from '../../statsig';

import {
    BASE_URL,
    CAPACITY,
    CHECKOUT_FORM_NAME,
    CHECKOUT_PATH,
    DEFAULT_COUNTRY,
    DELIVERY_METHODS_PATH,
    EXTERNAL_PAYMENT_PATH,
    PAYMENT_METHODS,
    STATUS_PATH,
    TICKETS_FULL_SIZE_PATH,
    TICKETS_WITH_CAPACITY_WAITLIST_PATH,
    WAITLIST_PATH,
    LOGIN_PATH,
} from '../../../constants';
import { OrderStatus } from '../../../models/order';
import {
    CONFIRM_TO_CHECKOUT_STATUS,
    CONFIRM_CLOSE_STATUS,
} from '../../../containers/status/constants';
import { ADD_TO_CART, triggerTrackingPixelAction } from '../../pixelAnalytics';
import {
    trackAbandonOrder,
    trackAbandonOrderCancel,
    trackAbandonOrderConfirm,
    trackBackClick,
    trackJoinWaitlistClick,
    trackTicketsPageSubmit,
    trackXPBackToCheckout,
} from '../../eventAnalytics';
import { getEventShippingSettings as getEventShippingSettingsAction } from '../../event';
import { startBackgroundTimer as startBackgroundTimerAction } from '../../timer';
import { resetWaitingRoomToken as resetWaitingRoomTokenAction } from '../../tokens';
import { resetWaitingRoom as resetWaitingRoomAction } from '../../waitingRoom';
import { updateRedirectInstructionsAction } from '../actions';
import { updateOrderAction } from '..';
import { OrderActionType } from '../types';
import { hasEnteredDonationAmount } from '../../../containers/ticketSelection/utils/ticketUtils';
import {
    getAttendees,
    getIsNosSourceOfTruth,
    getOrderId,
} from '../../../selectors/order/typedOrderSelectors';
import { getUserId, isUserLoggedIn } from '../../../selectors/user';
import { showPurchaseConfirmation as showPurchaseConfirmationAction } from '../../purchaseConfirmation';
import { CLEAR_PICK_A_SEAT_BEST_AVAILABLE_WARNING } from '../constants';
import {
    setUserSiginInMethodsAction,
    clearUserDataAction,
    setUserAsLoggedInAction,
    setLoginFromCheckoutPage,
} from '../../user';
import { handleOrderError, redirectToExternalPayment } from '../utils';
import {
    getIsTicketSelectionCompactSizeFlow,
    getIsAppRebrandActive,
} from '../../../selectors/app';
import { TicketsPageSize } from '../../../models/ticketsPage';
import { track } from '@eventbrite/datalayer-library';
import { HeapEventName } from '../../../constants/heap';
import { notifyParentOrderStatusPlaced } from '../../notifyParent';
import { getRoutePathname } from '../../../selectors/routing';
import { updateOrderAttendeesDefaultDeliveryMethodByCountry } from './updateOrderAttendeesDefaultDeliveryMethodByCountry';
import { shouldShowDeliveryMethodPage } from './shouldShowDeliveryMethodPage';
import { goToPageAfterReservedSeating } from './goToPageAfterReservedSeating';
import { handleUpdateOrder } from './handleUpdateOrder';
import { getAndSaveAvailablePaymentMethods } from './getAndSaveAvailablePaymentMethods';
import { handleUpdateOrderToken } from './handleUpdateOrderToken';
import { handleCaptchaBeforeCreateOrder } from './handleCaptchaBeforeCreateOrder';

export const clearPickASeatBestAvailableWarning = () => ({
    type: CLEAR_PICK_A_SEAT_BEST_AVAILABLE_WARNING,
});

const _getOrderFromState = (getState) => getState().order;

export const setUserSiginInMethods = () => async (dispatch, getState) => {
    const {
        user: { email },
    } = getState();

    if (email) {
        const { response } = await getUserAccountDetailsApi(email);

        if (response && response.sign_in_methods) {
            dispatch(setUserSiginInMethodsAction(response.sign_in_methods));
        }
    }
};

/**
 * Saves and places an order that is expected to be a redirected payment in NOS workflow (ex. iDEAL, Bancontact, etc...).
 * After placing the order, it will save the redirect instructions returned from the order service and return
 * them at the end of the promise chain.
 *
 * It should be only called within the order actions and no exported outside the folder.
 */
export const getRedirectedPaymentOrderPromiseWithAsyncNosWorkflow =
    (order) => async (dispatch) => {
        try {
            if (!order.redirectionInstructions) {
                // Redirect instructions are required, and if they are not available, that is a serious internal error.
                throw new CustomSubmissionError(
                    {
                        _error: gettext(
                            "There was an error redirecting you to your payment provider's site",
                        ),
                    },
                    {},
                );
            }

            await getPaymentHistoryApi(order.orderId);

            const updateOrderResponse = await updateOrderApi(order.orderId);

            await handleUpdateOrder(dispatch, updateOrderResponse.response);
            await handleUpdateOrderToken(dispatch, updateOrderResponse.headers);

            await dispatch(startBackgroundTimerAction());

            dispatch(
                updateRedirectInstructionsAction(order.redirectionInstructions),
            );
            dispatch(replace(joinPath(BASE_URL, EXTERNAL_PAYMENT_PATH)));

            return order.redirectionInstructions;
        } catch (error) {
            return handleOrderError(dispatch, error);
        }
    };

/**
 * Saves and places an order that is expected to be a redirected payment (ex. iDEAL, Bancontact, etc...).
 * After placing the order, it will save the redirect instructions returned from the order service and return
 * them at the end of the promise chain.
 *
 * It should be only called within the order actions and no exported outside the folder.
 */
export const getRedirectedPaymentOrderPromiseWithLosWorkflow = async ({
    dispatch,
    saveAndPlacePromise,
}) => {
    let redirectInstructions;
    let dispatchPayment;
    let updateOrderRequest;

    try {
        const placeOrderResponse = await saveAndPlacePromise;

        if ('redirect_instructions' in placeOrderResponse.response) {
            redirectInstructions =
                placeOrderResponse.response.redirect_instructions;

            if (redirectInstructions) {
                const orderIdNum = placeOrderResponse.response.id;

                dispatchPayment = () => getPaymentHistoryApi(orderIdNum);
                updateOrderRequest = () => updateOrderApi(orderIdNum);
            }
        } else {
            // Redirect instructions are required, and if they are not available, that is a serious internal error.
            throw new CustomSubmissionError(
                {
                    _error: gettext(
                        "There was an error redirecting you to your payment provider's site",
                    ),
                },
                {},
            );
        }

        await dispatch(dispatchPayment());

        const updateOrderResponse = await updateOrderRequest();

        await handleUpdateOrder(dispatch, updateOrderResponse.response);
        await handleUpdateOrderToken(dispatch, updateOrderResponse.headers);

        await dispatch(startBackgroundTimerAction());

        if (redirectInstructions) {
            dispatch(updateRedirectInstructionsAction(redirectInstructions));
            dispatch(replace(joinPath(BASE_URL, EXTERNAL_PAYMENT_PATH)));
        }

        return redirectInstructions;
    } catch (error) {
        return handleOrderError(dispatch, error);
    }
};

export const getOrderSuccessPromise = () => async (dispatch, getState) => {
    const state = getState();
    const {
        preOrderConfirmationSurveySettings: {
            hasQuestions: isOrderFormInTwoPages,
        },
    } = getSurvey(state);
    const isAppRebrandActive = getIsAppRebrandActive(state);

    dispatch(logOrderSuccessEvents());

    if (isAppRebrandActive) {
        dispatch(replace(getOrderSuccessAnimationPage()));
    } else {
        if (isOrderFormInTwoPages) {
            dispatch(replace(getOrganizerQuestionsPage()));
        } else {
            const platform = dispatch(notifyParentOrderStatusPlaced());

            if (isWebPlatform(platform)) {
                dispatch(showPurchaseConfirmationAction());
            }
        }
    }
    await dispatch(resetWaitingRoomAction());
    return dispatch(resetWaitingRoomTokenAction());
};

/**
 * Saves and places an order that will be paid by standard credit card (ex. Visa, Mastercard, etc...).
 * After placing the order, the app will continue to the order confirmation page.
 */

const _getCreditCardOrderPromise = async ({ dispatch, order }) => {
    try {
        await order.saveAndPlacePromise;
        return dispatch(getOrderSuccessPromise());
    } catch (error) {
        return Promise.reject(handleOrderError(dispatch, error));
    }
};

const _tryToRedirectToExternalPayment = (
    paymentMethod,
    order,
    bankId,
    getState,
    dispatch,
) => {
    const { availablePaymentMethods } = _getOrderFromState(getState);

    const isNosSourceOfTruth = getIsNosSourceOfTruth(getState());

    const getRedirectedPaymentOrderPromise = () =>
        isNosSourceOfTruth
            ? dispatch(
                  getRedirectedPaymentOrderPromiseWithAsyncNosWorkflow(order),
              )
            : getRedirectedPaymentOrderPromiseWithLosWorkflow(order);

    //FIXME the argument to new Promise probably shouldn't be an async function
    //eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
        await redirectToExternalPayment(
            paymentMethod,
            () => getRedirectedPaymentOrderPromise(order),
            availablePaymentMethods,
            bankId,
            getState,
            dispatch,
        ).catch((errorMessage) => {
            reject(
                new CustomSubmissionError({
                    _error: errorMessage,
                }),
            );
        });
        HAS_WINDOW_LISTENERS &&
            window.addEventListener('redirectError', (e) => {
                if (e && e.detail) {
                    reject(e.detail);
                }
                resolve();
            });
    });
};

const _requestIdealPayment = ({ order, getState, dispatch }) => {
    if (!order.formData.payment) {
        return new Promise(() => {
            throw new CustomSubmissionError(
                {
                    payment: { bankId: gettext('This field is required') },
                    _error: gettext(
                        'Please correct the highlighted fields below',
                    ),
                },
                {},
            );
        }).catch((error) => handleOrderError(dispatch, error));
    }

    return _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.IDEAL,
        order,
        order.formData.payment.bankId,
        getState,
        dispatch,
    );
};

const _requestNonceBraintreePayment = ({ order, dispatch, getState }) => {
    if (!order.formData.payment) {
        return new Promise(() => {
            throw new CustomSubmissionError(
                {
                    _error: gettext(
                        'You must authorize with PayPal before you can submit your order',
                    ),
                },
                {},
            );
        }).catch((error) => handleOrderError(dispatch, error));
    }
    return _getCreditCardOrderPromise({ order, dispatch, getState });
};

const _requestMaestroBancontactPayment = ({ order, getState, dispatch }) =>
    _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.MAESTRO_BANCONTACT,
        order,
        null,
        getState,
        dispatch,
    );

const _requestSofortPayment = ({ order, getState, dispatch }) =>
    _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.SOFORT,
        order,
        null,
        getState,
        dispatch,
    );

const _requestFPPPaypalPayment = ({ order, getState, dispatch }) =>
    _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.FPP_PAYPAL,
        order,
        null,
        getState,
        dispatch,
    );

const _requestSepaPayment = ({ order, dispatch }) => {
    const formFields = ['ownerName', 'iban', 'ibanAgreement'];
    const firstEmptyField = formFields
        .filter((field) => !get(order.formData.payment, field))
        .shift();

    if (firstEmptyField || !order.formData.payment) {
        return new Promise(() => {
            throw new CustomSubmissionError(
                {
                    payment: {
                        [firstEmptyField]: gettext('This field is required'),
                    },
                    _error: gettext(
                        'Please correct the highlighted fields below',
                    ),
                },
                {},
            );
        }).catch((error) => handleOrderError(dispatch, error));
    }

    return _getCreditCardOrderPromise({ order, dispatch });
};

const _requestStripePayment = ({ order, getState, dispatch }) =>
    _tryToRedirectToExternalPayment(
        PAYMENT_METHODS.STRIPE_CONFIRMATION_TOKEN,
        order,
        null,
        getState,
        dispatch,
    );

const paymentActions = {
    [PAYMENT_METHODS.IDEAL]: _requestIdealPayment,
    [PAYMENT_METHODS.MAESTRO_BANCONTACT]: _requestMaestroBancontactPayment,
    [PAYMENT_METHODS.NONCE_BRAINTREE]: _requestNonceBraintreePayment,
    [PAYMENT_METHODS.SOFORT]: _requestSofortPayment,
    [PAYMENT_METHODS.SEPA_DIRECT_DEBIT]: _requestSepaPayment,
    [PAYMENT_METHODS.FPP_PAYPAL]: _requestFPPPaypalPayment,
    [PAYMENT_METHODS.STRIPE_CONFIRMATION_TOKEN]: _requestStripePayment,
};

export const requestExternalPaymentWithNosWorkflow =
    (paymentMethod, order) => (dispatch, getState) =>
        paymentActions[paymentMethod]({
            order,
            getState,
            dispatch,
        }).catch((error) => handleOrderError(dispatch, error));

export const requestPayment =
    (paymentMethod, order) => (dispatch, getState) => {
        const action =
            paymentActions[paymentMethod] || _getCreditCardOrderPromise;
        return action({
            order,
            getState,
            dispatch,
        }).catch((error) => handleOrderError(dispatch, error));
    };

const _updateIfNewUserLoggedIn = async (dispatch, getState, userInfo) => {
    // There's a logged in user, check if it's the same than in the app state
    const state = getState();
    const userId = getUserId(state);

    if (userId !== userInfo.id) {
        // Not the same user, clear previous user and set the new one
        await dispatch(clearUserDataAction());
        return dispatch(setUserAsLoggedInAction(userInfo));
    }
};

export const updateLoggedInUserIfChanged = async (dispatch, getState) => {
    // Check against the API if the user in the App is still valid.
    // If not, update the state.
    try {
        const { response: userInfoResponse } =
            await getCurrentUserAccountDetails();

        return _updateIfNewUserLoggedIn(dispatch, getState, userInfoResponse);
    } catch (e) {
        // Check if the user is logged out. If so, only update if the app initialized with a user
        const parsedError = e?.extraData?.parsedError;
        const state = getState();

        if (parsedError?.error === 'NOT_AUTHORIZED' && isUserLoggedIn(state)) {
            dispatch(clearUserDataAction(state));
        }
    }
};

const statsigAbandonOrderEventValue = (getState) => {
    const state = getState();
    return getIsTicketSelectionCompactSizeFlow(state)
        ? TicketsPageSize.compact
        : TicketsPageSize.full;
};

/**
 * Try to abandon the order, and if something went wrong we will ignore the error
 * Order will be abandoned anyway when its TTL gets to 0 inside Redis
 */
export const abandonOrder = () => async (dispatch, getState) => {
    const state = getState();
    const orderId = getOrderId(state);

    if (!orderId) {
        return undefined;
    }

    try {
        dispatch(setLoginFromCheckoutPage(false));

        await abandonOrderApi(orderId);
        dispatch({
            type: OrderActionType.updateOrder,
            payload: {
                isPlaced: false,
            },
        });
        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_ALL_SUCCESS_ABANDON_ORDER,
            statsigAbandonOrderEventValue(getState),
        );
        return dispatch(trackAbandonOrderConfirm());
    } catch (e) {
        return undefined;
    }
};

/**
 * The user abandons the order by closing the browser tab or window before paying
 */
export const abandonOrderOnClose = () => (dispatch, getState) => {
    const {
        order: { id: orderId, status: orderStatus },
    } = getState();

    if (
        orderId &&
        (orderStatus === OrderStatus.started ||
            orderStatus === OrderStatus.pending)
    ) {
        abandonOrderApi(orderId);
        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_ALL_SUCCESS_ABANDON_ORDER,
            statsigAbandonOrderEventValue(getState),
        );
        return dispatch(trackAbandonOrderConfirm());
    }
};

/**
 * The order is not abandoned and the user remains in the checkout flow.
 * User will be taken back to a page of the given path, default is the checkout page.
 * A tracking action is also triggered to save the user action.
 */
export const backToPathOnCancelAbandonOrder =
    (pathName = CHECKOUT_PATH) =>
    (dispatch) => {
        dispatch(trackAbandonOrderCancel());

        return dispatch(push(joinPath(BASE_URL, pathName)));
    };

export const backToLogin = () => (dispatch) => {
    dispatch(replace(joinPath(BASE_URL, LOGIN_PATH)));
};

/**
 * The user has attempted to abandon the order by clicking the back button. This sends the user to a page
 * with a confirmation dialog and triggers a tracking action to save the user action.
 */
export const abandonOrderBackAttemptWithStatusPath =
    (
        pathName = CHECKOUT_PATH,
        statusPageSubPath = CONFIRM_TO_CHECKOUT_STATUS,
    ) =>
    (dispatch) => {
        dispatch(trackBackClick(pathName));
        return dispatch(
            push(joinPath(BASE_URL, STATUS_PATH, statusPageSubPath)),
        );
    };

export const goToPageBeforeCheckout = () => (dispatch, getState) => {
    const state = getState();
    const attendees = getAttendees(state);

    track({
        eventName: HeapEventName.previousPageClick,
        eventData: {
            pathname: getRoutePathname(getState()),
        },
    });

    if (shouldShowDeliveryMethodPage(attendees)) {
        dispatch(trackBackClick(CHECKOUT_PATH));
        return dispatch(push(joinPath(BASE_URL, DELIVERY_METHODS_PATH)));
    }

    return dispatch(abandonOrderBackAttemptWithStatusPath(CHECKOUT_PATH));
};

/**
 * The user has attempted to abandon the order by clicking the modal close button. This sends the user to a page
 * with a confirmation dialog and triggers a tracking action to save the user action.
 */
export const abandonOrderCloseAttemptWithStatusPath =
    ({ pathName, statusPath = CONFIRM_CLOSE_STATUS, fullPathName }) =>
    (dispatch) => {
        track({
            eventName: HeapEventName.modalCloseButtonClick,
            eventData: {
                pathname: fullPathName,
            },
        });
        dispatch(trackAbandonOrder(pathName));
        return dispatch(push(joinPath(BASE_URL, STATUS_PATH, statusPath)));
    };

/**
 * The user has attempted to back out of an external payment by clicking the back button. This sends the user
 * back to the checkout form page with all the fields filled out how they were before.
 */
export const backToCheckoutFromExternalPayment = () => (dispatch) => {
    dispatch(trackXPBackToCheckout());
    dispatch(setSubmitFailed(CHECKOUT_FORM_NAME));

    return dispatch(replace(joinPath(BASE_URL, CHECKOUT_PATH)));
};

/**
 * This is triggered when clicking "Checkout" from reserved event "Buy on Map" flow
 * It will make sure the selected tickets have correct delivery methods,
 * such as use backup method if shipping cut-off date has passed, and select the correct
 * default delivery method.
 */
export const checkoutFromBuyOnMap = () => async (dispatch, getState) => {
    const state = getState();
    const { order } = state;

    try {
        const { response: eligibility } = await getOrderEligibilityApi(
            order.id,
        );
        const orderWithEligibility = {
            ...order,
            eligibility,
        };

        dispatch(updateOrderAction(orderWithEligibility));

        if (eligibility.eligible) {
            // XXX: we are currently dispatching the getAndSaveAvailablePaymentMethods thunk in a couple
            // different places in the checkout flow where GA and Reserved diverged but we should only
            // have to do this at the last step before checkout (ie when the CheckoutPage is rendered).
            await dispatch(getAndSaveAvailablePaymentMethods());
            await dispatch(getEventShippingSettingsAction());
            await dispatch(
                updateOrderAttendeesDefaultDeliveryMethodByCountry(
                    DEFAULT_COUNTRY,
                ),
            );
            await dispatch(goToPageAfterReservedSeating());
        }
    } catch (e) {
        throw e;
    }
};

/**
 * Dispatch the correct actions on clicking Checkout from the Ticket Selection Page
 */
export const submitFromTicketSelectionPage =
    () => async (dispatch, getState) => {
        dispatch(triggerTrackingPixelAction(ADD_TO_CART));
        dispatch(trackTicketsPageSubmit(TICKETS_FULL_SIZE_PATH));
        await updateLoggedInUserIfChanged(dispatch, getState);

        return await dispatch(handleCaptchaBeforeCreateOrder(true));
    };

/**
 * This handles what onSubmit should do for events that have their event capacity waitlist triggered:
 * - Happy path: There are no unlimited donation tickets: clicking submit (Join Waitlist) should go to the regular Waitlist flow.
 * - With an unlimited donation ticket:
 *      - No donation amount entered: clicking submit (Join Waitlist) should go to the Waitlist flow.
 *      - When a donation amount is entered, clicking submit (Checkout) should dispatch the regular order actions to start an order.
 */
export const submitFromCapacityWaitlistTicketsPage =
    () => (dispatch, getState) => {
        const {
            tickets: { ticketsById },
        } = getState();

        if (hasEnteredDonationAmount(ticketsById)) {
            dispatch(submitFromTicketSelectionPage());
        } else {
            dispatch(
                trackJoinWaitlistClick(
                    TICKETS_WITH_CAPACITY_WAITLIST_PATH,
                    CAPACITY,
                ),
            );
            dispatch(push(joinPath(BASE_URL, WAITLIST_PATH)));
        }
    };
